import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { Plane, Calendar, Building, CheckCircle, CreditCard } from 'lucide-react';
import axiosInstance from '../../utils/axiosconfig';
import './PreviousBooking.css';

const PreviousBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async (cursor = null) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/booking/user-bookings?limit=10${cursor ? `&cursor=${cursor}` : ''}`);
      const newBookings = response.data.data.data;
      setBookings(prevBookings => cursor ? [...prevBookings, ...newBookings] : newBookings);
      setNextCursor(response.data.data.nextCursor);
    } catch (err) {
      setError('Failed to fetch bookings. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const loadMore = () => {
    if (nextCursor) {
      fetchBookings(nextCursor);
    }
  };

  if (error) {
    return <div className="text-center mt-5">{error}</div>;
  }

  return (
    <div className="previous-bookings-page">
      <section className="section-sub-banner bg-9 mb-4">
        <div className="awe-overlay"></div>
        <div className="sub-banner">
          <div className="container">
            <div className="text text-center">
              <h2>Your Previous Bookings</h2>
            </div>
          </div>
        </div>
      </section>

      <Container>
        {bookings.map((booking) => (
          <Card key={booking.id} className="mb-4">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={9}>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="route-info">
                      <h5 className="mb-0">AC Room </h5>
                      <small className="text-muted">  {'SRIHS' + booking.id.toString().padStart(4, '0')}  </small>
                    </div>
                    <Badge bg={booking.is_paid ? "success" : "warning"} className="ms-auto">
                      {booking.is_paid ? (
                        <><CheckCircle size={14} className="me-1" /> Paid</>
                      ) : (
                        <><CreditCard size={14} className="me-1" /> Pending Payment</>
                      )}
                    </Badge>
                  </div>

                  <Row className="g-4">
                    <Col md={4}>
                      <div className="d-flex align-items-center">
                        <Calendar size={20} className="text-primary me-2" />
                        <div>
                          <div className="text-muted small">Check-in</div>
                          <div className="fw-bold">{formatTime(booking.booking_start_date)}</div>
                          <div className="small">{formatDate(booking.booking_start_date)}</div>
                        </div>
                      </div>
                    </Col>

                    <Col md={4} className="text-center">
                      <div className="d-flex align-items-center justify-content-center">
                        <div>
                          <div className="text-muted small">Duration</div>
                          <div className="fw-bold">{booking.total_days} Days</div>
                        </div>
                      </div>
                    </Col>

                    <Col md={4}>
                      <div className="d-flex align-items-center justify-content-end">
                        <Calendar size={20} className="text-primary me-2" />
                        <div>
                          <div className="text-muted small">Check-out</div>
                          <div className="fw-bold">{formatTime(booking.booking_end_date)}</div>
                          <div className="small">{formatDate(booking.booking_end_date)}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>

                <Col md={3} className="text-end">
                  <div className="price-section">
                    <div className="text-muted small">Price</div>
                    <div className="h4 mb-2">₹{parseFloat(booking.total_amount).toFixed(2)}</div>
                  </div>
                </Col>
              </Row>

              {booking.special_comments && (
                <div className="mt-3 pt-3 border-top">
                  <small className="text-muted">Special Comments:</small>
                  <p className="mb-0 small">{booking.special_comments}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}

        {isLoading && (
          <div className="text-center my-4">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {nextCursor && !isLoading && (
          <div className="text-center mb-4">
            <Button onClick={loadMore} variant="outline-primary">
              Load More
            </Button>
          </div>
        )}

        {!isLoading && bookings.length === 0 && (
          <div className="text-center my-5">
            <h3>No bookings found</h3>
            <p className="text-muted">You haven't made any bookings yet.</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default PreviousBookingsPage;